import React, { useEffect, useMemo, memo, useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  createSession,
  getMontageId,
  getMontageFormError,
  getMontageFormLoading,
  getVisibleFormData,
} from './selectors'
import {
  DefaultJsonFormContextProps,
} from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getMontageEnums, getMontageForm } from '../../../store/montage/action-creators'
import { HStack } from '../../../ui/Stack'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import MontageTabs from './ui/tabs/montage-tabs'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { getMontageFormData } from './selectors'
import { MontageFormsHeader } from './ui/montage-forms/montage-form-header/montage-form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { Result, Skeleton } from 'antd'

function AdminNewObjectPage() {
  const { id } = useParams<{ id: string }>()
  if (!id) return null

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appealId = useSelector(getMontageId)
  const entityType = 'montage'
  const appealsFormIsLoading = useSelector(getMontageFormLoading)
  const appealsFormError = useSelector(getMontageFormError)
  const appealsFormData = useSelector(getMontageFormData)
  const montageFormData = useSelector(getMontageFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()

  const pageTitle = useMemo(
    () => `Монтаж №${id}`,
    [id],
  )

  const breadcrumbs = useMemo(() => [
    {
      href: '/montage',
      title: 'Монтаж',
    },
    {
      title: pageTitle,
    },
  ], [pageTitle])
  const [enumsRequested, setEnumsRequested] = useState(false)

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }), [id])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      setEnumsRequested(false)
      dispatch(getMontageForm(id))
    }
    return () => {
      mounted = false
    }
  }, [id, dispatch])

  useEffect(() => {
    if (appealsFormData && !appealsFormError && !appealsFormIsLoading && !enumsRequested) {
      setEnumsRequested(true)
      dispatch(getMontageEnums(id))
    }
  }, [appealsFormData, appealsFormError, appealsFormIsLoading, enumsRequested, id, dispatch])

  useEffect(() => {
    if (id === 'new' && appealId) {
      navigate({
        pathname: `/montage/${appealId}`,
      })
    }
    return () => {
    }
  }, [id, appealId])

  const handleToOrder = useMemo(() => async () => {
    await saveResult(id, visibleFormData, montageFormData?.stages?.current, true, undefined)
  }, [id, montageFormData, visibleFormData, saveResult])

  const handleToFinish = useMemo(() => async () => {
    await saveResult(id, visibleFormData, montageFormData?.stages?.current, true, true)
  }, [id, montageFormData, visibleFormData, saveResult])

  const setCaseStage = useCallback(async (data) => {
    await saveResult(id, data, montageFormData?.stages?.current, true, false)
  }, [id, montageFormData, saveResult])

  const handleToNotAccepted = useCallback(async (data) => {
    const formData = visibleFormData && visibleFormData?.resultObject
    await setCaseStage({ resultObject: { ...formData, ...data, to_stage: 'not_accepted' } })
  }, [visibleFormData, setCaseStage])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {appealsFormIsLoading && <Skeleton active />}
      {!appealsFormIsLoading && appealsFormError && (
        <Result
          status={typeof appealsFormError === 'object' ? appealsFormError.statusCode : 'error'}
          title='Ошибка'
          subTitle={
            typeof appealsFormError === 'object' ? appealsFormError.message : appealsFormError
          }
        />
      )}
      {!appealsFormIsLoading && !appealsFormError && appealsFormData && (
        <>
          <DefaultJsonFormContext.Provider value={defaultProps}>
            <MontageFormsHeader
              id={id}
              isFinished={montageFormData?.stages && montageFormData?.stages?.current === Object.keys(montageFormData?.stages?.items).length - 1}
              stage={montageFormData?.stages?.current}
              toOrder={handleToOrder}
              toFinish={handleToFinish}
              toNotAccepted={handleToNotAccepted}
              showTitle={false}
            />
            <MontageTabs id={id} />
          </DefaultJsonFormContext.Provider>
        </>
      )}
    </B2BLayout>
  )
}

export default memo(AdminNewObjectPage)
